/**
 * useOrder — fetches a single order and polls every 15s while the order is
 * still in a non-terminal state. Used by the tracking screen.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { orderApi } from "@/api/services";
import { OrderStatusEnum } from "@/api/online-ordering-service/model";
import type { OrderOutput } from "@/api/online-ordering-service/model";

const TERMINAL_STATUSES: ReadonlySet<OrderStatusEnum> = new Set([
  OrderStatusEnum.Delivered,
  OrderStatusEnum.Completed,
  OrderStatusEnum.Cancelled,
  OrderStatusEnum.Rejected,
]);

export function isTerminalStatus(s: OrderStatusEnum | undefined | null): boolean {
  return !!s && TERMINAL_STATUSES.has(s);
}

const POLL_INTERVAL_MS = 15_000;

export type UseOrderResult = {
  order: OrderOutput | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
};

export type UseOrderOptions = {
  /**
   * When false the hook is inert: no request is issued, no poll timer is
   * armed, `loading` is false and `error` is null. Callers use this to hold
   * the fetch until a usable identity exists (an anonymous dine-in diner has
   * no Authorization header until they mint a payer session, so an eager
   * fetch guarantees a 401). Defaults to true so existing callers are
   * unaffected.
   */
  enabled?: boolean;
};

export function useOrder(
  orderId: string | undefined,
  opts?: UseOrderOptions,
): UseOrderResult {
  const enabled = opts?.enabled ?? true;
  const active = !!orderId && enabled;
  const [order, setOrder] = useState<OrderOutput | null>(null);
  const [loading, setLoading] = useState<boolean>(active);
  const [error, setError] = useState<Error | null>(null);
  const cancelledRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!orderId || !enabled) return;
    try {
      const o = await orderApi.get(orderId);
      if (!cancelledRef.current) {
        setOrder(o);
        setError(null);
      }
    } catch (err) {
      if (!cancelledRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, [orderId, enabled]);

  useEffect(() => {
    cancelledRef.current = false;
    if (!orderId || !enabled) {
      setOrder(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    fetchOnce();

    const interval = setInterval(() => {
      // Stop polling once we reach a terminal status — but check at fire
      // time using the latest state via setOrder's callback form.
      setOrder((current) => {
        if (isTerminalStatus(current?.status)) {
          clearInterval(interval);
          return current;
        }
        // Trigger refresh asynchronously (don't await inside setState).
        fetchOnce();
        return current;
      });
    }, POLL_INTERVAL_MS);

    return () => {
      cancelledRef.current = true;
      clearInterval(interval);
    };
  }, [orderId, enabled, fetchOnce]);

  return { order, loading, error, refresh: fetchOnce };
}
